import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import LeadKanbanBoard from '../../components/leads/LeadKanbanBoard';
import LeadFormModal from '../../components/leads/LeadFormModal';
import LeadFilterBar from '../../components/leads/LeadFilterBar';
import RevenueTimeline from '../../components/leads/RevenueTimeline';
import ProjectTimeline from '../../components/leads/ProjectTimeline';
import { InviteExternalViewerModal } from '../../components/leads/InviteExternalViewerModal';
import {
  useLeads,
  useCreateLead,
  useUpdateLead,
  useLeadLabels,
  useLeadSources,
  useLeadLostReasons,
} from '../../hooks/useLeads';
import {
  LEAD_FILTER_URL_PARAM_KEYS,
  filterLeads,
  parseLeadFiltersFromUrl,
  serializeLeadFiltersToParams,
  type LeadBoardFilters,
} from '../../utils/leadBoardFilters';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { AppToast, type AppToastState } from '../../components/app-access/AppToast';
import type { Lead, CreateLeadInput, UpdateLeadInput, StageTotals } from '../../services/api/leads';
import { weightedValue } from '../../services/api/leadStages';
import { apiGet } from '../../services/api/client';
import { getExchangeRates, convertToCZK } from '../../services/exchangeRate';
import { useAuth } from '../../services/authContext';
import {
  useCreateLeadStage,
  useUpdateLeadStage,
  useDeleteLeadStage,
} from '../../hooks/useLeadStages';

export default function LeadsPage() {
  const { data, isLoading } = useLeads();
  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const { user, isAdmin, isSales } = useAuth();

  // MKS-347 — marketing. The route admits admin, sales and marketing, the API
  // lets marketing READ leads and refuses every write, so a viewer who is
  // neither admin nor sales gets the board with nothing on it that would be a
  // 403.
  const readOnly = !isAdmin && !isSales;

  // Editing the pipeline is admin or sales (the role this app gives the
  // people who run it; there is no separate sales-manager role). The backend
  // gate is `requireSales` on `/api/lead-stages`; this only decides whether
  // the affordances are drawn.
  const canManageStages = isAdmin || isSales;

  const createStage = useCreateLeadStage();
  const updateStage = useUpdateLeadStage();
  const deleteStage = useDeleteLeadStage();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  // Phase 105 (Plan 105-07, D-07): Tasks-parity grant surface, admin-only.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<AppToastState | null>(null);

  const { data: labelsData } = useLeadLabels();
  const allLabels = useMemo(() => labelsData?.data ?? [], [labelsData]);
  // MKS-347 — the source list for the filter bar, and the lost-reason list
  // the board's lost-move modal picks from. A read-only viewer opens neither
  // write surface, so they do not fetch the reasons.
  const { data: sourcesData } = useLeadSources();
  const allSources = useMemo(() => sourcesData?.data ?? [], [sourcesData]);
  const { data: lostReasonsData } = useLeadLostReasons(!readOnly);
  const lostReasons = lostReasonsData?.data;

  // MKS-347 — the filter state, hydrated from the URL on the very first
  // render so a pasted link already shows the filtered board, and written
  // back by the effect below. Same contract as the ticket board (MKS-168):
  // merge into the EXISTING params so nothing else on the URL is clobbered,
  // and `{ replace: true }` so typing a search character does not push a
  // history entry per keystroke.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<LeadBoardFilters>(() =>
    parseLeadFiltersFromUrl(searchParams),
  );

  useEffect(() => {
    setSearchParams(
      (prev) => {
        for (const key of LEAD_FILTER_URL_PARAM_KEYS) prev.delete(key);
        for (const [key, value] of Object.entries(serializeLeadFiltersToParams(filters))) {
          prev.set(key, value);
        }
        return prev;
      },
      { replace: true },
    );
  }, [filters, setSearchParams]);

  // F-1206: only admins may call the admin settings endpoint. Non-admin sales
  // users would get a 403 on every render, spamming the server with retries.
  // Default to 14 when the current user cannot read admin settings.
  const { data: settings } = useQuery({
    queryKey: ['adminSettings'],
    queryFn: () => apiGet<{ data: Record<string, unknown> }>('/admin/settings'),
    enabled: isAdmin,
  });
  const staleDays = (settings?.data?.lead_staleness_days as number) ?? 14;

  const today = new Date().toISOString().split('T')[0]!;
  const { data: exchangeRates } = useQuery({
    queryKey: ['exchangeRates', today],
    queryFn: () => getExchangeRates(today),
    staleTime: 24 * 60 * 60 * 1000,
  });

  const handleCardClick = (lead: Lead) => {
    // MKS-347 — the only lead destination this page has is the edit modal,
    // and every save behind it is a 403 for a read-only viewer. Nothing to
    // open, so nothing happens.
    if (readOnly) return;
    setEditingLead(lead);
    setIsFormOpen(true);
  };

  const handleCreate = (input: CreateLeadInput) => {
    createLead.mutate(input, {
      onSuccess: () => {
        setIsFormOpen(false);
        setEditingLead(null);
      },
    });
  };

  const handleUpdate = (id: number, input: UpdateLeadInput) => {
    updateLead.mutate({ id, input }, {
      onSuccess: () => {
        setIsFormOpen(false);
        setEditingLead(null);
      },
    });
  };

  const leads = useMemo(() => data?.data ?? [], [data]);

  // MKS-345 — the configured pipeline rides along on the lead list response.
  const stages = useMemo(() => data?.stages ?? [], [data]);

  // MKS-347 — the owner picker is built from the leads themselves rather than
  // `/leads/sales-users`: that endpoint is sales-only (403 for marketing) and
  // an owner with no leads is not worth a filter chip anyway.
  const owners = useMemo(() => {
    const byId = new Map<number, { id: number; firstName: string | null; lastName: string | null }>();
    for (const lead of leads) {
      if (lead.owner && !byId.has(lead.owner.id)) byId.set(lead.owner.id, lead.owner);
    }
    return [...byId.values()].sort((a, b) =>
      `${a.lastName ?? ''}${a.firstName ?? ''}`.localeCompare(`${b.lastName ?? ''}${b.firstName ?? ''}`),
    );
  }, [leads]);

  const filteredLeads = useMemo(
    () => filterLeads(leads, filters, { currentUserId: user?.id ?? null }),
    [leads, filters, user?.id],
  );

  const handleFiltersChange = useCallback((next: LeadBoardFilters) => setFilters(next), []);

  const dealValueInCZK = useMemo(() => {
    const map = new Map<number, number>();
    for (const lead of leads) {
      const raw = parseFloat(lead.dealValue ?? '0') || 0;
      const currency = lead.dealValueCurrency ?? 'CZK';
      if (currency === 'CZK' || !exchangeRates) {
        map.set(lead.id, raw);
      } else {
        const rate = exchangeRates.get(currency) ?? 1;
        map.set(lead.id, convertToCZK(raw, currency, rate));
      }
    }
    return map;
  }, [leads, exchangeRates]);

  const stageTotals = useMemo<StageTotals>(() => {
    const totals: StageTotals = {};
    for (const stage of stages) {
      // Totals reflect the filtered view so column counts match visible cards.
      const stageLeads = filteredLeads.filter((l) => l.stageId === stage.id);
      // F13-003: values are FX-converted to CZK on the page side, so emit
      // a single-currency map. The component renders per-currency totals
      // and degrades cleanly if FX conversion is later removed.
      const total = stageLeads.reduce((sum, l) => sum + (dealValueInCZK.get(l.id) ?? 0), 0);
      const value: Record<string, number> = total ? { CZK: total } : {};
      totals[String(stage.id)] = {
        count: stageLeads.length,
        value,
        // The column weights this itself from the stage's probability; carried
        // here so the shape matches what the server sends.
        weightedValue: weightedValue(value, stage.probability),
      };
    }
    return totals;
  }, [filteredLeads, dealValueInCZK, stages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
        <div className="flex items-center gap-2">
          {/* MKS-347 — a read-only viewer gets a board, not a toolbar: every
              control here opens a write surface the API would refuse. */}
          {readOnly ? (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
              Read-only
            </span>
          ) : (
            <>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  External users
                </button>
              )}
              <Button
                onClick={() => {
                  setEditingLead(null);
                  setIsFormOpen(true);
                }}
              >
                Add Lead
              </Button>
            </>
          )}
        </div>
      </div>

      {/* MKS-347 — search and filters, URL-persisted. The label chips live
          inside the bar. */}
      <LeadFilterBar
        filters={filters}
        onChange={handleFiltersChange}
        owners={owners}
        sources={allSources}
        stages={stages}
        labels={allLabels}
        canFilterMine={user?.id !== undefined}
        visibleCount={filteredLeads.length}
        totalCount={leads.length}
      />

      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-16">
          <p className="text-gray-500 mb-4">
            {readOnly
              ? 'No leads yet.'
              : 'No leads yet. Create your first lead to get started.'}
          </p>
          {!readOnly && (
            <Button
              onClick={() => {
                setEditingLead(null);
                setIsFormOpen(true);
              }}
            >
              Create your first lead
            </Button>
          )}
        </div>
      ) : (
        <LeadKanbanBoard
          leads={filteredLeads}
          stages={stages}
          stageTotals={stageTotals}
          staleDays={staleDays}
          dealValueCZKMap={dealValueInCZK}
          onCardClick={handleCardClick}
          readOnly={readOnly}
          lostReasons={lostReasons}
          configurable
          // Handed down only to those allowed to edit — the board draws the
          // "+ Add stage" cell and the per-column controls off their presence.
          onAddStage={
            canManageStages
              ? (name, color) => createStage.mutate({ name, color })
              : undefined
          }
          onRenameStage={
            canManageStages ? (id, name) => updateStage.mutate({ id, name }) : undefined
          }
          onRecolorStage={
            canManageStages ? (id, color) => updateStage.mutate({ id, color }) : undefined
          }
          onDeleteStage={canManageStages ? (id) => deleteStage.mutate(id) : undefined}
        />
      )}

      <RevenueTimeline leads={filteredLeads} exchangeRates={exchangeRates} onLeadClick={handleCardClick} />
      <ProjectTimeline leads={filteredLeads} onLeadClick={handleCardClick} />

      {/* MKS-347 — not even mounted for a read-only viewer: it is the write
          surface this page exists to offer, and `handleCardClick` can never
          open it for them anyway. */}
      {!readOnly && (
      <LeadFormModal
        lead={editingLead}
        isOpen={isFormOpen}
        isLoading={createLead.isPending || updateLead.isPending}
        onClose={() => {
          setIsFormOpen(false);
          setEditingLead(null);
        }}
        onSubmitCreate={handleCreate}
        onSubmitUpdate={handleUpdate}
      />
      )}

      {isAdmin && (
        <InviteExternalViewerModal
          isOpen={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onSuccess={(message) => setToast({ variant: 'success', message })}
          onError={(message) => setToast({ variant: 'error', message: `Couldn’t update access: ${message}` })}
        />
      )}

      <AppToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
